/**
 * Composable для работы с API комнат
 *
 * Отвечает за:
 * - Создание новых комнат
 * - Присоединение к существующим комнатам
 * - Работу с токенами доступа
 */

import { ref, type Ref } from 'vue';

/** Ответ от API при создании комнаты */
interface CreateRoomResponse {
  roomId: string;
  token: string;
  joinUrl: string;
}

/** Ответ от API при присоединении к комнате */
interface JoinRoomResponse {
  token: string;
}

export function useRoomApi() {
  // ==================== СОСТОЯНИЕ ====================

  /** Флаг загрузки */
  const loading: Ref<boolean> = ref(false);

  /** Сообщение об ошибке */
  const error: Ref<string> = ref('');

  // ==================== API МЕТОДЫ ====================

  /**
   * Создает новую комнату
   *
   * @param displayName - Имя пользователя
   * @returns Данные созданной комнаты (ID, токен, URL)
   */
  async function createRoom(displayName: string): Promise<CreateRoomResponse | null> {
    if (!displayName.trim()) {
      error.value = 'Введите имя';
      return null;
    }

    loading.value = true;
    error.value = '';

    try {
      const response = await fetch('/api/rooms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          displayName: displayName.trim()
        })
      });

      if (!response.ok) {
        throw new Error('Не удалось создать комнату');
      }

      const data: CreateRoomResponse = await response.json();

      // Сохраняем токен в localStorage для последующего входа
      localStorage.setItem(`token_${data.roomId}`, data.token);

      return data;

    } catch (err) {
      error.value = (err as Error).message;
      return null;

    } finally {
      loading.value = false;
    }
  }

  /**
   * Присоединяется к существующей комнате
   *
   * @param roomId - ID комнаты
   * @param displayName - Имя пользователя
   * @returns Токен доступа
   */
  async function joinRoom(roomId: string, displayName: string): Promise<string | null> {
    if (!displayName.trim()) {
      error.value = 'Введите имя';
      return null;
    }

    loading.value = true;
    error.value = '';

    try {
      const response = await fetch(`/api/rooms/${roomId}/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          displayName: displayName.trim()
        })
      });

      if (!response.ok) {
        throw new Error('Не удалось войти в комнату');
      }

      const data: JoinRoomResponse = await response.json();

      // Сохраняем токен в localStorage
      localStorage.setItem(`token_${roomId}`, data.token);

      return data.token;

    } catch (err) {
      error.value = (err as Error).message;
      return null;

    } finally {
      loading.value = false;
    }
  }

  /**
   * Получает токен для комнаты из localStorage
   *
   * @param roomId - ID комнаты
   * @returns Токен или null
   */
  function getStoredToken(roomId: string): string | null {
    return localStorage.getItem(`token_${roomId}`);
  }

  /**
   * Удаляет токен комнаты из localStorage
   *
   * @param roomId - ID комнаты
   */
  function removeStoredToken(roomId: string): void {
    localStorage.removeItem(`token_${roomId}`);
  }

  /**
   * Очищает ошибку
   */
  function clearError(): void {
    error.value = '';
  }

  // ==================== ВОЗВРАЩАЕМЫЙ API ====================

  return {
    // Состояние
    loading,
    error,

    // Методы
    createRoom,
    joinRoom,
    getStoredToken,
    removeStoredToken,
    clearError
  };
}
